import { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { apiGet } from "../lib/api";
import type { OutreachRecipient } from "../lib/types";
import { useScope } from "../scope/ScopeContext";

/**
 * Team recognition — every "any team members you'd like to recognize?" answer,
 * newest first.
 *
 * The store already gets an email the moment one is submitted, but that mail is
 * gone the day it arrives. This is the browsable record: the compliment, who it
 * came from, and which store. It is the one piece of survey output a GM wants
 * to read on purpose rather than be alerted about, and the only one that is
 * good news.
 *
 * Read-only by design — there is nothing to action here, so there is no
 * acknowledge workflow to clutter it.
 */

export function OutreachRecognitionPage() {
  const { focusedDealerId } = useScope();
  const [recipients, setRecipients] = useState<OutreachRecipient[]>([]);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams({ hasRecognition: "true", limit: "200" });
      if (focusedDealerId) params.set("dealerId", focusedDealerId);
      const res = await apiGet<{ recipients: OutreachRecipient[] }>(
        `/api/outreach/recipients?${params.toString()}`
      );
      setRecipients(
        res.recipients.sort((a, b) => (b.respondedAt ?? "").localeCompare(a.respondedAt ?? ""))
      );
    } catch (err) {
      console.error("recognition list load failed:", err);
    } finally {
      setLoading(false);
    }
  }, [focusedDealerId]);

  useEffect(() => {
    void load();
  }, [load]);

  return (
    <div className="mx-auto max-w-4xl px-4 py-6 sm:px-8 sm:py-8">
      <div>
        <h1 className="text-2xl font-semibold text-slate-900">Team recognition</h1>
        <p className="mt-1 text-sm text-slate-500">
          {recipients.length === 0
            ? "Compliments customers left for your people."
            : `${recipients.length} compliment${recipients.length === 1 ? "" : "s"} customers left for your people — worth reading out at a meeting.`}
        </p>
      </div>

      <div className="mt-6 space-y-3">
        {loading && <div className="text-sm text-slate-500">Loading…</div>}
        {!loading && recipients.length === 0 && (
          <div className="rounded-xl border border-dashed border-slate-300 bg-white px-5 py-8 text-center text-sm text-slate-500">
            No recognitions yet. They arrive when a customer answers yes and names someone.
          </div>
        )}
        {recipients.map((r) => {
          const name = [r.firstName, r.lastName].filter(Boolean).join(" ") || "(no name)";
          return (
            <article
              key={r.id}
              className="rounded-xl border border-emerald-200 bg-emerald-50 p-4 shadow-sm"
            >
              <div className="flex items-start justify-between gap-3">
                <div>
                  <Link
                    to={`/outreach/recipients/${r.id}`}
                    className="text-sm font-semibold text-slate-900 hover:underline"
                  >
                    {name}
                  </Link>
                  <span className="ml-2 text-xs text-slate-500">
                    {r.dealerName} · {r.type} ·{" "}
                    {r.respondedAt ? new Date(r.respondedAt).toLocaleString() : ""}
                  </span>
                </div>
              </div>
              <blockquote className="mt-2 whitespace-pre-wrap text-sm text-slate-800">
                {r.teamMemberRecognition}
              </blockquote>
            </article>
          );
        })}
      </div>
    </div>
  );
}
